import React from 'react';
import { Stack, Text, Skeleton, Card } from '@mantine/core';
import DataChartSection from '../echarts/DataChartSection';
import type { SeriesPoint } from '../../types';
import type { TFunction } from 'i18next';

type ShapesLR = Record<string, 'line' | 'step'>;

type Props = {
  t: TFunction<'translation'>;
  // context
  isMobile: boolean;
  isTablet: boolean;
  seriesItems: SeriesPoint[];
  baseMetrics: string[];
  translateMetricId: (id: string) => string;
  translateSeriesLabel: (label: string) => string;
  unitForMetric: (base: string) => string;
  // split context
  effectiveSensorIds: string[];
  gatewayIds: string[];
  // settings
  metricSettings: Record<string, { color: string; axis: 'left' | 'right' | 'both'; type: 'line' | 'boxplot' | 'step' | 'arearange' }>;
  weatherMetrics: string[];
  colors: Record<string, string>;
  interval: string;
  dateOnlyTicks: boolean;
  unitAxisSides: Record<string, 'left' | 'right'>;
  weatherShapes: Record<string, 'line' | 'step' | 'bar'>;
  showTrends: boolean;
  trendTargets: 'all' | 'overlay' | 'base';
  // candlestick
  canCandle: boolean;
  candleBase: string | undefined;
  // loading state
  loading?: boolean;
};

const MetricChartBlock: React.FC<Props> = ({
  t: _t,
  isMobile,
  isTablet,
  seriesItems,
  baseMetrics,
  translateMetricId,
  translateSeriesLabel,
  unitForMetric,
  effectiveSensorIds,
  gatewayIds,
  metricSettings,
  weatherMetrics,
  colors,
  interval,
  dateOnlyTicks,
  unitAxisSides,
  weatherShapes,
  showTrends,
  trendTargets,
  canCandle,
  candleBase,
  loading = false,
}) => {
  // Show skeleton loader when loading
  if (loading) {
    return (
      <Stack>
        {[...Array(2)].map((_, i) => (
          <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
            <Skeleton height={20} width={150} mb="md" />
            <Skeleton height={300} />
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Stack>
      {baseMetrics.map((bm) => {
        const isCandleHere = canCandle && candleBase === bm;
        const labelsAll = (effectiveSensorIds.length >= 1
          ? effectiveSensorIds.map((sid) => `${bm} (${sid})`)
          : (gatewayIds.length > 1 ? gatewayIds.map((gid) => `${bm} (${gid})`) : [bm])
        );
        const labelsFinal = weatherMetrics.length > 0 ? [...labelsAll, ...weatherMetrics] : labelsAll;
        const displayNameMap = Object.fromEntries(labelsFinal.map((l) => [l, translateSeriesLabel(l)]));

        if (isCandleHere) {
          const overlayWeather = weatherMetrics.length > 0 ? weatherMetrics : [];
          return (
            <Stack key={`cm-${bm}`} gap={4}>
              <Text fw={600}>{translateMetricId(bm)}</Text>
              <DataChartSection
                mode="boxplot"
                data={seriesItems}
                metrics={labelsAll}
                colors={colors as Record<string, string>}
                resolution={interval}
                dateOnlyTicks={dateOnlyTicks}
                height={isMobile ? 880 : 1080}
                showLegend
                displayNameMap={displayNameMap}
                overlayLines={overlayWeather.length ? {
                  metrics: overlayWeather,
                  colors: colors as Record<string, string>,
                  shapes: Object.fromEntries(overlayWeather.map((wm) => [wm, (weatherShapes[wm] || 'line')])) as Record<string, 'line' | 'step' | 'bar'>,
                } : undefined}
                unitAxisSides={unitAxisSides}
                showTrends={showTrends}
                trendTargets={trendTargets}
              />
            </Stack>
          );
        }

        const palette2 = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4'];
        const colors2 = Object.fromEntries(labelsFinal.map((k, i) => {
          const base = k.includes(' (') ? k.substring(0, k.indexOf(' (')) : k;
          const col = metricSettings[base]?.color || palette2[i % palette2.length];
          return [k, col];
        }));
        const shapes2 = Object.fromEntries(labelsFinal.map((k) => {
          const base = k.includes(' (') ? k.substring(0, k.indexOf(' (')) : k;
          const shape = (metricSettings[base]?.type === 'step') ? 'step' : 'line';
          return [k, shape];
        })) as ShapesLR;
        const axes2 = Object.fromEntries(labelsFinal.map((label) => {
          const base = label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label;
          const ax = (metricSettings[base]?.axis ?? 'left') as 'left' | 'right' | 'both';
          return [label, ax];
        })) as Record<string, 'left' | 'right' | 'both'>;
        const units2 = Object.fromEntries(labelsFinal.map((label) => {
          const base = label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label;
          return [label, unitForMetric(base)];
        })) as Record<string, string>;
        const isAreaRangeHere = metricSettings[bm]?.type === 'arearange';

        if (isAreaRangeHere) {
          return (
            <Stack key={`ar-${bm}`} gap={4}>
              <Text fw={600}>{translateMetricId(bm)}</Text>
              <DataChartSection
                mode="arearange"
                data={seriesItems}
                metrics={labelsFinal}
                colors={colors2}
                units={units2}
                resolution={interval}
                dateOnlyTicks={dateOnlyTicks}
                height={isMobile ? 480 : (isTablet ? 600 : 680)}
                showLegend
                displayNameMap={displayNameMap}
                overlayLines={weatherMetrics.length > 0 ? {
                  metrics: weatherMetrics,
                  colors: colors as Record<string, string>,
                  shapes: Object.fromEntries(weatherMetrics.map((m) => [m, (weatherShapes[m] || 'line')])) as Record<string, 'line' | 'step' | 'bar'>,
                } : undefined}
                unitAxisSides={unitAxisSides}
                showTrends={showTrends}
                trendTargets={trendTargets}
              />
            </Stack>
          );
        }

        return (
          <Stack key={`lm-${bm}`} gap={4}>
            <Text fw={600}>{translateMetricId(bm)}</Text>
            <DataChartSection
              mode="line"
              data={seriesItems}
              metrics={labelsFinal}
              colors={colors2}
              axes={axes2}
              shapes={shapes2}
              units={units2}
              resolution={interval}
              dateOnlyTicks={dateOnlyTicks}
              height={isMobile ? 480 : (isTablet ? 600 : 680)}
              showLegend
              displayNameMap={displayNameMap}
              overlayLines={weatherMetrics.length > 0 ? {
                metrics: weatherMetrics,
                colors: colors as Record<string, string>,
                shapes: Object.fromEntries(weatherMetrics.map((m) => [m, (weatherShapes[m] || 'line')])) as Record<string, 'line' | 'step' | 'bar'>,
              } : undefined}
              unitAxisSides={unitAxisSides}
              showTrends={showTrends}
              trendTargets={trendTargets}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export default MetricChartBlock;
